import { Outlet, createFileRoute, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Spinner } from '@/src/components/ui/spinner'
import { useAuth } from '@/src/components/AuthContextProvider'
import { getOperationType } from '@/src/api/operation-type'
import { DynamicForm } from '@/src/components/manage/forms/DynamicForm'

export const Route = createFileRoute('/_manage/manage/admin/operations/types_/$typeId')({
  component: AdminOperationTypeViewPage,
})

function AdminOperationTypeViewPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { typeId } = useParams({ from: '/_manage/manage/admin/operations/types_/$typeId' })
  const { isAdmin } = useAuth()
  const [mode, setMode] = useState<'basic' | 'expert'>('basic')

  useEffect(() => {
    if (!isAdmin) {
      navigate({ to: '/not-permitted' })
    }
  }, [isAdmin, navigate])

  const { data: operationType, isLoading } = useQuery({
    queryKey: ['operationType', typeId],
    queryFn: () => getOperationType({ data: { id: typeId } }),
    enabled: !!typeId,
  })

  if (location.pathname.endsWith('/edit')) {
    return <Outlet />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!operationType) {
    return (
      <div className="p-6 text-muted-foreground">
        Operation type not found.
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/manage/admin/operations/types/$typeId/edit', params: { typeId: operationType.id } })}
          >
            Edit
          </Button>
        </AdminToolbar.Left>
        <AdminToolbar.Right>
          <div className="flex items-center gap-2">
            <Button
              variant={mode === 'basic' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('basic')}
            >
              Basic
            </Button>
            <Button
              variant={mode === 'expert' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('expert')}
            >
              Expert
            </Button>
          </div>
        </AdminToolbar.Right>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto">
          <DynamicForm
            schema={operationType.formSchema}
            locale="en"
            mode={mode}
            onModeChange={setMode}
            showModeToggle={false}
            readOnly={true}
            showActions={false}
            onSubmit={() => undefined}
          />
        </div>
      </div>
    </div>
  )
}
